import '~/polyfills';
import { ToastProvider } from '@carvana/ds-toast';
import { useEffectOnce } from '@carvana/merch-react-hooks';
import cx from 'classnames';
import type { AppProps as NextAppProps } from 'next/app';
import Head from 'next/head';
import { useEffect } from 'react';
import GoogleTagManager from '@/common/GoogleTagManager';
import useMicroFrontend from '@/common/MicroFrontEnds';
import SpurMonocleScript from '@/common/SpurMonocleScript';
import ClientId from '~/constants/ClientId';
import { assetsCdnUrl } from '~/constants/environment';
import type { DeviceTypeProps } from '~/server-props/common/contracts';
import initDataDog from '~/utilities/datadog';

import '~/styles/global.css';

export type AppProps = {
  clientId: ClientId;
  correlationId: string;
  dataDogReplaySampleRate?: number;
  dataDogSampleRate?: number;
  deviceTypeProps?: DeviceTypeProps;
  disableGTM?: boolean;
  enableMfeDebug?: boolean;
  enableSpurMonocle?: boolean;
  gtmId: string | string[];
};

function getIsSafari(browserName: string): boolean {
  return browserName.includes('safari') || browserName.includes('ios');
}

export default function App(props: NextAppProps<AppProps>) {
  const { Component, pageProps } = props;
  const isSafari = getIsSafari(pageProps.deviceTypeProps?.browserName ?? '');
  const isShaqvana = Component.name === 'Shaqvana';

  useEffectOnce(() => {
    initDataDog(pageProps.correlationId);
  }, [pageProps.correlationId]);

  useMicroFrontend(pageProps.enableMfeDebug ?? false);

  useEffect(() => {
    if (isSafari) import('focus-visible');
  }, [isSafari]);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="initial-scale=1.0, width=device-width"
          key="viewport"
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={`${assetsCdnUrl}/icons/apple/apple-touch-icon.png`}
          crossOrigin=""
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={`${assetsCdnUrl}/icons/favicon-32x32.png`}
          crossOrigin=""
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={`${assetsCdnUrl}/icons/favicon-16x16.png`}
          crossOrigin=""
        />
        <link
          rel="icon"
          type="image/x-icon"
          href={`${assetsCdnUrl}/icons/favicon.ico`}
          crossOrigin=""
        />
        <link
          href={`${assetsCdnUrl}/fonts/BrandonText-Light.woff2`}
          type="font/woff2"
          crossOrigin=""
        />
        <link
          href={`${assetsCdnUrl}/fonts/BrandonText-Bold.woff2`}
          type="font/woff2"
          crossOrigin=""
        />
        <link
          href={`${assetsCdnUrl}/fonts/BrandonText-Regular.woff2`}
          type="font/woff2"
          crossOrigin=""
        />
        <link
          href="https://assets.fastly.carvana.io/fonts/BrandonforCarvana-Black.woff2"
          type="font/woff2"
          crossOrigin=""
        />
      </Head>
      <ToastProvider>
        <div
          className={cx('mx-auto max-w-[1800px]', {
            'max-w-full': isShaqvana
          })}
          data-page-client-id={pageProps.clientId}
        >
          <Component {...pageProps} />
        </div>
      </ToastProvider>
      {(Array.isArray(pageProps.gtmId)
        ? pageProps.gtmId
        : [pageProps.gtmId]
      ).map(gtmId => (
        <GoogleTagManager
          key={gtmId}
          disableGTM={pageProps.disableGTM}
          gtmId={gtmId}
          isNative={
            pageProps.deviceTypeProps?.nativeDeviceType?.isNative ?? false
          }
        />
      ))}
      <SpurMonocleScript enabled={Boolean(pageProps.enableSpurMonocle)} />
    </>
  );
}
